import { Dispatch } from "react";
import { ActionIcon, Button, Divider, Drawer, Group, Loader, Stack, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { MdDashboard, MdOutlineOpenInNew, MdOutlineSettings, MdOutlineShare, MdRefresh } from "react-icons/md";
import { Action } from "../../hooks/useSettings";
import { DeveloperSetting } from "../developer-setting";
import { TokenClaimsView } from "../token-claims-view";
import { useHistoryManagerContext } from "../HistoryProviders/HistoryManagerContext";
import { ChatHistory } from "../chat-history/ChatHistory";
import { useNavigate } from "react-router-dom";
import { V2ChatHistoryApiResponse, V2ChatHistoryTurn } from "../../api";
import { useAuthorization } from "../../AuthorizationContext";

export interface TopNavProps {
    isAnyQuestionAsked: boolean;
    conversationId?: string | null;
    shareableContent: V2ChatHistoryTurn[];
    onNewConversationClick: () => void;
    onSettingsChange: Dispatch<Action>;
    notifty: boolean;
    onChatHistoryItemClick: (item: V2ChatHistoryApiResponse) => void;
}

export const TopNav = ({
    isAnyQuestionAsked,
    conversationId,
    onSettingsChange,
    onNewConversationClick,
    shareableContent,
    notifty: notify,
    onChatHistoryItemClick
}: TopNavProps) => {
    const [settingsOpened, { open: openSettings, close: closeSettings }] = useDisclosure(false);
    const { t } = useTranslation();
    const historyManager = useHistoryManagerContext();
    const { isAdmin, loading: authorizationLoading } = useAuthorization();
    const navigate = useNavigate();

    const handleShareConversationClick = async () => {
        // TODO v2 - check format of text
        const message = shareableContent
            .map(
                chat =>
                    `Me:\n${chat.userMessage}\n\nArthrex Intelligence:\n${chat.response?.response?.replace(/<a [^>]*><sup>\d+<\/sup><\/a>|<[^>]+>/g, "")}
    \n\n\n`
            )
            .join("");
        await navigator.share({
            title: "Arthrex Intelligance Conversation",
            text: message
        });
    };

    return (
        <>
            <Group justify="space-between">
                {isAnyQuestionAsked ? (
                    <Button leftSection={<MdRefresh size={24} />} onClick={onNewConversationClick}>
                        {t("newConversation")}
                    </Button>
                ) : (
                    <span></span>
                )}
                <Group justify="flex-end">
                    {isAdmin && (
                        <ActionIcon variant="transparent" color="dark.4" onClick={openSettings}>
                            <MdOutlineSettings size={24} />
                        </ActionIcon>
                    )}
                    <ChatHistory historyManager={historyManager} notify={notify} onChatHistoryItemClick={onChatHistoryItemClick} />
                    {isAnyQuestionAsked && (
                        <ActionIcon variant="transparent" color="dark.4" onClick={handleShareConversationClick}>
                            <MdOutlineShare size={24} />
                        </ActionIcon>
                    )}
                    {authorizationLoading && <Loader color="dark" size="xs" />}
                    {isAdmin && conversationId && (
                        <Tooltip label="View in admin history">
                            <ActionIcon component="a" href={`/#/administration/history/${conversationId}`} target="_blank" variant="transparent" color="dark.4">
                                <MdOutlineOpenInNew size={24} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                    {isAdmin && (
                        <ActionIcon variant="transparent" color="dark.4" onClick={() => navigate("/administration")}>
                            <MdDashboard size={24} />
                        </ActionIcon>
                    )}
                </Group>
            </Group>
            <Drawer position="right" withOverlay={true} offset={8} radius="md" opened={settingsOpened} onClose={closeSettings} title={t("labels.headerText")}>
                <Stack gap="sm">
                    <DeveloperSetting onSettingsChange={onSettingsChange} />
                    <Divider my="md" />
                    <TokenClaimsView />
                </Stack>
            </Drawer>
        </>
    );
};
